import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useTheme } from '../../contexts/ThemeContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();

  const navigationItems = [
    { label: 'Home', path: '/home-landing', icon: 'Home' },
    { label: 'Services', path: '/services-overview', icon: 'Package' },
    { label: 'Locations', path: '/collection-points', icon: 'MapPin' },
    { label: 'Contact', path: '/contact-us', icon: 'Phone' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleNavigation = (path) => {
    window.location.href = path;
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <>
      {/* OLD transparent header styles (commented):
          Background: isScrolled ? 'bg-card/95 backdrop-blur-sm shadow-elevation-2' : 'bg-card/80 backdrop-blur-xs'
          Nav inactive: 'text-foreground hover:bg-muted hover:text-foreground'
          Logo: 'text-foreground', tagline: 'text-muted-foreground'
          Theme/menu buttons: 'text-foreground hover:bg-muted'
          Mobile nav inactive: 'text-foreground hover:bg-muted' */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-smooth bg-white ${isScrolled ? 'shadow-elevation-2' : 'shadow-sm'
          }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div
              className="flex items-center cursor-pointer transition-smooth hover:opacity-80"
              onClick={() => handleNavigation('/home-landing')}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Shirt" size={20} color="white" />
                </div>
                <div className="flex flex-col">
                  {/* OLD: text-foreground, text-muted-foreground */}
                  <span className="text-lg font-semibold text-primary leading-tight">
                    Maayo Fabric Care
                  </span>
                  <span className="text-xs text-primary font-mono leading-none">
                    PREMIUM FABRIC CARE
                  </span>
                </div>
              </div>
            </div>

            {/* Desktop Navigation - Centered */}
            <nav className="hidden md:flex items-center space-x-2 absolute left-1/2 transform -translate-x-1/2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`px-5 py-2 rounded-lg text-sm font-medium transition-smooth flex items-center space-x-2 ${isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-elevation-1'
                    : 'text-primary hover:bg-primary/10 hover:text-primary'
                    }`}
                >
                  <Icon name={item?.icon} size={16} />
                  <span>{item?.label}</span>
                </button>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-3">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-primary hover:bg-primary/10 transition-smooth"
                title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
                aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
              >
                <Icon
                  name={isDarkMode ? "Sun" : "Moon"}
                  size={18}
                />
              </button>

              <Button
                variant="outline"
                size="sm"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => handleNavigation('/pickup-scheduling')}
                className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-smooth hover:hover-scale"
              >
                Schedule Pickup
              </Button>
              <p
                href="tel:+919600044004"
                className="text-primary hover:text-primary/80 transition-colors font-medium"
              >
                +91 96000 44004
              </p>
            </div>

            {/* Mobile Actions */}
            <div className="flex items-center space-x-2 md:hidden">
              {/* Mobile Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-primary hover:bg-primary/10 transition-smooth"
                title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
                aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
              >
                <Icon
                  name={isDarkMode ? "Sun" : "Moon"}
                  size={20}
                />
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-lg text-primary hover:bg-primary/10 transition-smooth"
                aria-label="Toggle mobile menu"
              >
                <Icon
                  name={isMobileMenuOpen ? "X" : "Menu"}
                  size={24}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm"
            onClick={toggleMobileMenu}
          />
          <div className="fixed top-16 left-0 right-0 bottom-0 bg-card shadow-elevation-3 animate-slide-in">
            <div className="flex flex-col h-full">
              {/* Navigation Items */}
              <nav className="flex-1 px-4 py-6 space-y-2">
                {navigationItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-smooth ${isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-elevation-1'
                      : 'text-primary hover:bg-primary/10'
                      }`}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-medium">{item?.label}</span>
                  </button>
                ))}
              </nav>

              {/* Mobile CTA Section */}
              <div className="px-4 py-6 border-t border-border space-y-3">
                <Button
                  variant="outline"
                  fullWidth
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={() => handleNavigation('/services-overview')}
                  className="border-primary text-primary hover:bg-primary/10 hover:text-primary"
                >
                  Schedule Pickup
                </Button>
                <a
                  href="tel:+919600044004"
                  className="text-primary hover:text-primary/80 transition-colors font-medium w-full text-center py-3"
                >
                  +91 96000 44004
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spacer for fixed header */}
      <div className="h-16" />
    </>
  );
};

export default Header;